import { z } from 'zod';
import { isCompetence, isISODate } from './dates';
import { MAX_CENTS } from './money';
import { ALL_PERMISSIONS } from './permissions';

/** Contratos de entrada da API. O backend valida com estes mesmos esquemas. */

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const isoDate = z.string().refine(isISODate, 'Data inválida');
export const competence = z.string().refine(isCompetence, 'Competência inválida (AAAA-MM)');
export const uuid = z.uuid('Identificador inválido');
export const optionalUuid = z.uuid().nullish().transform((v) => v ?? null);
export const cents = z.number().int('Valor deve estar em centavos').min(0, 'Valor não pode ser negativo').max(MAX_CENTS);
export const positiveCents = cents.refine((v) => v > 0, 'Informe um valor maior que zero');

export const email = z.string().trim().toLowerCase().pipe(z.email('E-mail inválido')).pipe(z.string().max(160));
export const password = z
  .string()
  .min(10, 'A senha precisa ter pelo menos 10 caracteres')
  .max(200)
  .refine((v) => /[A-Za-z]/.test(v) && /\d/.test(v), 'Use letras e números na senha');

// ── Autenticação ────────────────────────────────────────────────────────────
export const loginSchema = z.object({
  email,
  password: z.string().min(1).max(200),
  /** Login pela página da empresa: ativa essa empresa se houver vínculo. */
  tenantSlug: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{1,60}$/).nullish(),
});

export const signupSchema = z.object({
  name: trimmed(120).min(2, 'Informe seu nome'),
  email,
  password,
  company: z.object({
    name: trimmed(160).min(2, 'Informe o nome da empresa'),
    document: optionalText(32),
  }),
});

export const forgotPasswordSchema = z.object({ email });
export const resetPasswordSchema = z.object({ token: z.string().min(20).max(200), password });
export const switchCompanySchema = z.object({ tenantId: uuid });

// ── Lançamentos (pagar/receber) ─────────────────────────────────────────────
export const priority = z.enum(['low', 'normal', 'high', 'urgent']);

export const entryBase = z.object({
  partyId: uuid,
  description: trimmed(240).min(2, 'Descreva o lançamento'),
  categoryId: optionalUuid,
  costCenterId: optionalUuid,
  competence,
  dueDate: isoDate,
  amountCents: cents.nullable(), // null = valor ainda não informado (fica pendente de revisão)
  bankAccountId: optionalUuid,
  priority: priority.default('normal'),
  notes: optionalText(2000),
  documentNumber: optionalText(80),
  contractRef: optionalText(120),
});

export const entryCreateSchema = entryBase.extend({
  installments: z.number().int().min(1).max(120).default(1),
  /** Intervalo em meses entre parcelas. */
  installmentIntervalMonths: z.number().int().min(1).max(12).default(1),
});

export const entryUpdateSchema = entryBase.partial().extend({
  version: z.number().int().min(1),
  changeReason: optionalText(500),
});

export const settlementSchema = z
  .object({
    date: isoDate,
    bankAccountId: uuid,
    principalCents: cents,
    interestCents: cents.default(0),
    fineCents: cents.default(0),
    discountCents: cents.default(0),
    proofAttachmentId: optionalUuid,
    notes: optionalText(1000),
  })
  .refine((v) => v.principalCents + v.interestCents + v.fineCents > 0, {
    message: 'O valor pago precisa ser maior que zero',
    path: ['principalCents'],
  });

export const adjustmentSchema = z.object({
  date: isoDate,
  discountCents: positiveCents,
  reason: trimmed(500).min(3, 'Informe o motivo do ajuste'),
});

export const reverseSchema = z.object({ reason: trimmed(500).min(3, 'Informe o motivo do estorno') });
export const cancelSchema = z.object({ reason: trimmed(500).min(3, 'Informe o motivo do cancelamento') });

export const approvalDecisionSchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
    note: optionalText(1000),
  })
  .refine((v) => v.decision === 'approve' || (v.note && v.note.length >= 3), {
    message: 'Justifique a recusa',
    path: ['note'],
  });

export const requestApprovalSchema = z.object({ note: optionalText(1000) });

// ── Recorrências ─────────────────────────────────────────────────────────────
export const recurringSchema = z.object({
  kind: z.enum(['payable', 'receivable']).default('payable'),
  partyId: optionalUuid,
  description: trimmed(240).min(2),
  categoryId: optionalUuid,
  costCenterId: optionalUuid,
  bankAccountId: optionalUuid,
  amountCents: cents.nullable(),
  dayOfMonth: z.number().int().min(1).max(31),
  startCompetence: competence,
  endCompetence: competence.nullish().transform((v) => v ?? null),
  priority: priority.default('normal'),
  status: z.enum(['active', 'pending_review', 'paused']).default('active'),
  notes: optionalText(2000),
});

export const generateRecurringSchema = z.object({ competence });

// ── Bancos ──────────────────────────────────────────────────────────────────
export const bankAccountSchema = z.object({
  name: trimmed(80).min(2, 'Informe um nome para a conta'),
  bankName: optionalText(80),
  agency: optionalText(20),
  accountNumber: optionalText(30),
  initialBalanceCents: z.number().int().min(-MAX_CENTS).max(MAX_CENTS).nullable(),
  initialBalanceDate: isoDate.nullable(),
  isActive: z.boolean().default(true),
}).refine((v) => (v.initialBalanceCents === null) === (v.initialBalanceDate === null), {
  message: 'Informe o saldo inicial e a data de referência juntos',
  path: ['initialBalanceDate'],
});

export const transferSchema = z
  .object({
    fromAccountId: uuid,
    toAccountId: uuid,
    date: isoDate,
    amountCents: positiveCents,
    description: optionalText(240),
    feeCents: cents.default(0),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, { message: 'Escolha contas diferentes', path: ['toAccountId'] });

// ── Cadastros ───────────────────────────────────────────────────────────────
export const partySchema = z.object({
  name: trimmed(160).min(2, 'Informe o nome'),
  document: optionalText(32),
  email: z.string().trim().toLowerCase().max(160).nullish().transform((v) => v || null).pipe(z.email('E-mail inválido').nullable()),
  phone: optionalText(40),
  pixKey: optionalText(120),
  bankDetails: optionalText(500),
  isClient: z.boolean().default(false),
  isSupplier: z.boolean().default(false),
  notes: optionalText(2000),
  isActive: z.boolean().default(true),
}).refine((v) => v.isClient || v.isSupplier, { message: 'Marque cliente, fornecedor ou ambos', path: ['isSupplier'] });

export const categorySchema = z.object({
  name: trimmed(80).min(2),
  kind: z.enum(['expense', 'income']),
  isActive: z.boolean().default(true),
});

export const costCenterSchema = z.object({
  name: trimmed(120).min(2),
  code: optionalText(30),
  type: z.enum(['project', 'cost_center']),
  clientId: optionalUuid,
  isActive: z.boolean().default(true),
});

// ── Configurações da empresa ────────────────────────────────────────────────
export const companyProfileSchema = z.object({
  name: trimmed(160).min(2),
  legalName: optionalText(200),
  document: optionalText(32),
  timeZone: trimmed(60).min(3),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use o formato #RRGGBB').nullish().transform((v) => v ?? null),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use o formato #RRGGBB').nullish().transform((v) => v ?? null),
  highlightColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use o formato #RRGGBB').nullish().transform((v) => v ?? null),
  requireApprovalForPayment: z.boolean(),
  requireProofForPayment: z.boolean(),
  segregateApproval: z.boolean(),
});

export const roleSchema = z.object({
  name: trimmed(80).min(2),
  description: optionalText(240),
  permissions: z.array(z.enum(ALL_PERMISSIONS as [string, ...string[]])).max(ALL_PERMISSIONS.length),
});

export const memberInviteSchema = z.object({
  name: trimmed(120).min(2),
  email,
  roleId: uuid,
});

export const memberUpdateSchema = z.object({
  roleId: uuid.optional(),
  status: z.enum(['active', 'disabled']).optional(),
});

// ── Plataforma ──────────────────────────────────────────────────────────────
const limitValue = z.number().int().min(0).max(1_000_000).nullable();

export const planSchema = z.object({
  code: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{2,40}$/, 'Use letras minúsculas, números e hífen'),
  name: trimmed(80).min(2),
  description: optionalText(500),
  priceCents: cents.nullable(),
  priceApproved: z.boolean().default(false),
  interval: z.enum(['monthly', 'yearly']),
  trialDays: z.number().int().min(0).max(90).default(14),
  limits: z.object({ users: limitValue, bankAccounts: limitValue, entriesPerMonth: limitValue, storageMb: limitValue }),
  features: z.object({
    approvals: z.boolean(),
    recurring: z.boolean(),
    imports: z.boolean(),
    reportsExport: z.boolean(),
    attachments: z.boolean(),
  }),
  isActive: z.boolean().default(true),
});

export const platformTenantCreateSchema = z.object({
  name: trimmed(160).min(2),
  document: optionalText(32),
  timeZone: trimmed(60).default('America/Sao_Paulo'),
  planId: uuid,
  owner: z.object({ name: trimmed(120).min(2), email }),
  trialDays: z.number().int().min(0).max(90).nullish(),
});

export const platformTenantUpdateSchema = z.object({
  name: trimmed(160).min(2).optional(),
  document: optionalText(32).optional(),
  responsibleUserId: optionalUuid.optional(),
});

export const reasonSchema = z.object({ reason: trimmed(500).min(3, 'Informe o motivo') });

export const subscriptionActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('activate'), periodEnd: isoDate, reason: optionalText(500) }),
  z.object({ action: z.literal('extend_trial'), trialEndsAt: isoDate, reason: optionalText(500) }),
  z.object({ action: z.literal('change_plan'), planId: uuid, reason: trimmed(500).min(3, 'Informe o motivo') }),
  z.object({ action: z.literal('renew'), periodEnd: isoDate, reason: optionalText(500) }),
  z.object({ action: z.literal('mark_past_due'), reason: trimmed(500).min(3, 'Informe o motivo') }),
  z.object({ action: z.literal('suspend'), reason: trimmed(500).min(3, 'Informe o motivo') }),
  z.object({ action: z.literal('cancel'), reason: trimmed(500).min(3, 'Informe o motivo') }),
  z.object({ action: z.literal('reactivate'), periodEnd: isoDate, reason: trimmed(500).min(3, 'Informe o motivo') }),
]);

export const platformInvoiceSchema = z.object({
  amountCents: positiveCents,
  dueDate: isoDate,
  periodStart: isoDate,
  periodEnd: isoDate,
  reference: optionalText(120),
});

export const platformInvoicePaySchema = z.object({ paidAt: isoDate, reference: optionalText(120) });

export const platformUserSchema = z.object({
  email,
  name: trimmed(120).min(2),
  role: z.enum(['superadmin', 'operator', 'viewer']),
});

export type EntryCreateInput = z.infer<typeof entryCreateSchema>;
export type EntryUpdateInput = z.infer<typeof entryUpdateSchema>;
export type SettlementInput = z.infer<typeof settlementSchema>;
export type PlanInput = z.infer<typeof planSchema>;
export type SubscriptionAction = z.infer<typeof subscriptionActionSchema>;
